import { useQuery } from "@tanstack/react-query";

import { get } from "api";
import { DiscountAppliesTo, DiscountType, Models } from "api/types";
import OfferIcon from "atoms/icon/icons/ic_offers_ol.svg";
import RewardsIcon from "atoms/icon/icons/ic_rewards_ol.svg";
import { DiscountDetails } from "molecules/discount/DiscountDetails";
import { FreePackageGroupDetails } from "molecules/free-package-group/FreePackageGroupDetails";
import { useFeatureFlag } from "services/Flags";
import { isBuyXGetYDiscount } from "services/HelperService";
import { getInclusionsCount } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";

interface PromotionDetailsProps {
  inclusions?: Models<"InclusionListingsModel">;
  discount?: Models<"DiscountModel">;
  customDisclaimer?: string;
  freePackageGroup?: Models<"FreePackageGroupModel">;
}
export const PromotionDetails = (props: PromotionDetailsProps) => {
  const isBuyXGetYFlagEnabled = useFeatureFlag("BuyXGetY");
  const { data: memberships, isLoading: areMembershipsLoading } = useQuery({
    queryKey: ["/memberships"],
    queryFn: () => get("/memberships"),
    staleTime: Infinity,
  });

  const { data: listings, isLoading: areListingsLoading } = useQuery({
    queryKey: ["/listings"],
    queryFn: () => get("/listings"),
    staleTime: Infinity,
  });

  const { data: giftCards, isLoading: areGiftCardsLoading } = useQuery({
    queryKey: ["/gift-cards"],
    queryFn: () => get("/gift-cards", {}),
    staleTime: Infinity,
  });
  const giftCardItems = giftCards?.flatMap((g) => g.giftCards);

  const requirements = props.discount?.requirements;
  const isBuyXGetY =
    isBuyXGetYFlagEnabled && isBuyXGetYDiscount(props.discount);
  const hasFreePackageGroup = !!props.freePackageGroup;

  const {
    minimumQuantity,
    minimumSpendAmount,
    packageId: requirementsPackageId,
  } = requirements?.find(
    (r) => r.minimumQuantity > 0 || r.minimumSpendAmount > 0 || r.packageId
  ) || {};

  const inclusionsCount = getInclusionsCount(props.inclusions);

  const listingPackages = listings
    ?.map((l) => l.packages)
    .flat()
    .map((p) => p);

  const packageRequirements = listingPackages?.find(
    (lp) => lp.id === requirementsPackageId
  );

  const giftCardRequirements = giftCardItems?.find(
    (g) => g.packageId === requirementsPackageId
  );

  const action = minimumQuantity
    ? `Purchase ${minimumQuantity}`
    : `Spend ${formatCurrency(minimumSpendAmount ?? 0, 0)} on`;

  const treatmentMessage =
    props.inclusions?.treatments
      ?.filter((t) => t.quantityLimit)
      ?.flatMap((t) =>
        t.variants.length
          ? t.variants.map((v) => `${t.quantityLimit} of ${v.name}`)
          : [`${t.quantityLimit} of ${t.name}`]
      ) ?? [];

  const packageMessage =
    props.inclusions?.packages
      ?.filter((p) => p.quantityLimit)
      ?.map((p) => `${p.quantityLimit} of ${p.name}`) ?? [];

  const getInclusionMessage = () => {
    const lastMessage = allMessages[allMessages.length - 1];
    const initialMessages = allMessages.slice(0, -1).join(", ");
    const conjunction = allMessages.length > 1 ? ", and " : "";
    return initialMessages + conjunction + lastMessage;
  };

  const allMessages = [...(treatmentMessage || []), ...(packageMessage || [])];
  const inclusionMessage = allMessages.length ? getInclusionMessage() : "";

  const isGiftCard = !!giftCardRequirements;
  const packageRequirementName = isGiftCard
    ? `Gift Card${(minimumQuantity ?? 0) > 1 ? "s" : ""} of $${
        giftCardRequirements?.baseCostInCents / 100
      }`
    : packageRequirements?.name;
  const discountBaseMessage = isBuyXGetY
    ? `${action} ${packageRequirementName}, and receive ${inclusionMessage}`
    : "Apply this offer in cart to receive";

  const discountDetailsTitle = getDiscountDetailTitle(
    !!memberships?.map((m) => m.active).length,
    discountBaseMessage,
    `Scan the QR code in-office and show to staff to claim this offer!`,
    isBuyXGetY,
    props.discount
  );

  const freePackageGroupDetailsTitle = getFreePackageDetailTitle(
    "Redeem this offer to receive",
    props.freePackageGroup,
    inclusionsCount
  );

  if (areMembershipsLoading || areListingsLoading) return null;

  return (
    <div className="flex flex-col gap-6">
      {hasFreePackageGroup ? (
        <FreePackageGroupDetails
          {...props}
          title={freePackageGroupDetailsTitle}
          itemType={"offer"}
        />
      ) : (
        <DiscountDetails
          {...props}
          icon={isBuyXGetY ? OfferIcon : RewardsIcon}
          isBuyXGetY={isBuyXGetY}
          title={discountDetailsTitle}
          itemType={"offer"}
        />
      )}
    </div>
  );
};

const getDiscountDetailTitle = (
  includeMemberships: boolean,
  baseMessage: string,
  inOfficeDiscountTitle: string,
  isBuyXGetY: boolean,
  discount?: Models<"DiscountModel">
) => {
  const membershipText = includeMemberships
    ? ` or membership (1st month only)`
    : "";

  if (!discount) {
    return inOfficeDiscountTitle;
  }

  const isAppliesToAll = discount.appliesTo === DiscountAppliesTo.AppliesToAll;
  const isCashDiscount = discount.type === DiscountType.Cash;
  const isPercentageDiscount = discount.type === DiscountType.Percentage;

  const amountOff = discount.amountOff / 100;
  const percentOff = (discount.percentOff * 100).toFixed(0);

  const buyXGetYAmountOffText = `at $${amountOff} off.`;
  const buyXGetYPercentOffText = `at ${percentOff}% off.`;

  if (isAppliesToAll) {
    if (isCashDiscount) {
      return isBuyXGetY
        ? `${baseMessage} ${buyXGetYAmountOffText}`
        : `${baseMessage} $${amountOff} off your next purchase in-app!`;
    }
    if (isPercentageDiscount && discount.percentOff !== 1) {
      return isBuyXGetY
        ? `${baseMessage} ${buyXGetYPercentOffText}`
        : `${baseMessage} ${percentOff}% off any service${membershipText} in-app!`;
    }

    if (isPercentageDiscount && discount.percentOff === 1) {
      return `${baseMessage} as a complimentary gift.`;
    }

    return `${baseMessage} 1 free service ${membershipText} in-app!`;
  }

  if (isPercentageDiscount && discount.percentOff !== 1) {
    return isBuyXGetY
      ? `${baseMessage} ${buyXGetYPercentOffText}`
      : `${baseMessage} ${percentOff}% off of the following services:`;
  }

  if (isCashDiscount) {
    return isBuyXGetY
      ? `${baseMessage} ${buyXGetYAmountOffText}`
      : `${baseMessage} $${amountOff} off when you purchase at least one of the following services in-app:`;
  }

  if (isBuyXGetY && discount.percentOff === 1) {
    return `${baseMessage} as a complimentary gift.`;
  }

  return `${baseMessage} 1 of the following services for free:`;
};

const getFreePackageDetailTitle = (
  baseMessage: string,
  freePackageGroup?: Models<"FreePackageGroupModel">,
  inclusionsCount?: number
) => {
  if (!freePackageGroup || !inclusionsCount) return "";
  const isChooseFrom = freePackageGroup.hasSelectableInclusions;
  const isSingleItem =
    !freePackageGroup.hasSelectableInclusions && inclusionsCount === 1;
  const isIncludeAll =
    !freePackageGroup.hasSelectableInclusions && inclusionsCount > 1;
  if (isChooseFrom)
    return `${baseMessage} ${freePackageGroup.numberOfSelections} of the following services for free:`;
  if (isSingleItem) return `${baseMessage} the following service for free:`;
  if (isIncludeAll) return `${baseMessage} the following services for free:`;
  return baseMessage;
};
